import React from "react"
import PropTypes from "prop-types"
import { Button, Flex, Skeleton } from "antd"
import { useTranslation } from "react-i18next"
import { Icon } from "ui/Icon"
import { formatBookingTimeRange } from "../../utils"
import styles from "./index.module.css"

/**
 * The day's reservations for this listing.
 *
 * A row is a link to the reservation rather than an expander: everything a guide would do about a
 * booking lives on its own page, and the panel's job here is only to get them there.
 */
const DayReservations = ({ bookings, isLoading, onOpenBooking, onViewAll }) => {
  const { t } = useTranslation()

  if (isLoading) return <Skeleton active paragraph={{ rows: 3 }} title={false} />
  if (!bookings.length) return null

  return (
    <Flex vertical gap="var(--spacing-s)">
      <Flex align="center" justify="space-between" gap="var(--spacing-s)" wrap>
        <span className={styles.sectionHeading}>
          {t("availability.reservations_count", { defaultValue: "Reservations ({{count}})", count: bookings.length })}
        </span>
        <Button color="primary" variant="outlined" shape="round" onClick={onViewAll}>
          {t("availability.view_reservations_page", { defaultValue: "View Reservations Page" })}
        </Button>
      </Flex>

      <div>
        {bookings.map((booking) => (
          <button key={booking.uuid || booking.id} type="button" className={styles.bookingRow} onClick={() => onOpenBooking(booking)}>
            <Flex vertical gap="var(--spacing-xxs)" align="flex-start" className={styles.bookingMain}>
              <span className={styles.bookingTime}>{formatBookingTimeRange(booking)}</span>
              <span className={styles.bookingMeta}>
                {[
                  booking.client_name,
                  booking.total_guest_count
                    ? t("availability.guests_count", { defaultValue: "{{count}} guest", count: booking.total_guest_count })
                    : null
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </Flex>
            {booking.booking_number && <span className={styles.bookingNumber}>{`#${booking.booking_number}`}</span>}
            <Icon name="ChevronRight" width={20} height={20} color="var(--color-text-secondary-dark)" />
          </button>
        ))}
      </div>
    </Flex>
  )
}

DayReservations.propTypes = {
  bookings: PropTypes.arrayOf(
    PropTypes.shape({
      uuid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      client_name: PropTypes.string,
      start_time: PropTypes.string,
      end_time: PropTypes.string,
      total_guest_count: PropTypes.number,
      booking_number: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
  ).isRequired,
  isLoading: PropTypes.bool,
  onOpenBooking: PropTypes.func.isRequired,
  /** Opens the reservations page filtered to this listing and day. */
  onViewAll: PropTypes.func.isRequired
}

export default DayReservations
